/**
 * SubscriptionStatus Component
 * 
 * Displays current subscription status and management options.
 * - Shows plan status (free/trialing/active/lifetime)
 * - "Manage Subscription" button opens Polar portal
 * - "Upgrade to Lifetime" for monthly subscribers
 * 
 * See: POLAR_MANAGE.md for documentation
 */

import { useState, useCallback } from 'react';
import { useAuth } from '@services/auth';
import { useUpgrade, SubscriptionStatus as SubStatus } from '@hooks/useUpgrade';
import { openPortal } from '@services/subscription';
import { Card, CardHeader, Button } from '@/components/shared';
import { formatDate as formatDateIntl } from '@/utils/format';
import './SubscriptionStatus.css';

// Format date for display
function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  try {
    // App-language locale (was browser locale) - utils/format.ts central rule
    return formatDateIntl(new Date(dateStr), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

// Get status badge variant
function getStatusVariant(status: SubStatus): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'lifetime':
    case 'active':
      return 'success';
    case 'trialing':
      return 'warning';
    case 'past_due':
      return 'error';
    case 'cancelled':
      return 'error';
    default:
      return 'default';
  }
}

// Format status for display
function formatStatus(status: SubStatus): string {
  switch (status) {
    case 'lifetime':
      return 'Lifetime';
    case 'active':
      return 'Active';
    case 'trialing':
      return 'Trial';
    case 'past_due':
      return 'Past Due';
    case 'cancelled':
      return 'Cancelled';
    case 'free':
    default:
      return 'Free';
  }
}

interface SubscriptionStatusProps {
  onUpgrade: () => void;
}

export function SubscriptionStatus({ onUpgrade }: SubscriptionStatusProps) {
  const { session, user } = useAuth();
  const { subscriptionStatus } = useUpgrade();
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);

  // Get trial end date from user metadata
  const trialEndDate = user?.user_metadata?.trialEndDate;
  
  // Check if subscription is set to cancel at period end
  // This would come from a fresh status fetch, but we can approximate from metadata
  const polarCustomerId = user?.user_metadata?.polarCustomerId;
  const canManage = !!polarCustomerId;

  // Handle opening portal
  const handleManageSubscription = useCallback(async () => {
    if (!session?.access_token) {
      setPortalError('Please sign in to manage your subscription');
      return;
    }

    setPortalLoading(true);
    setPortalError(null);

    const result = await openPortal(session.access_token);
    
    setPortalLoading(false);
    
    if (!result.success) {
      setPortalError(result.error || 'Failed to open subscription portal');
    }
  }, [session?.access_token]);

  // Don't show for free users (they have upgrade prompts elsewhere)
  if (subscriptionStatus === 'free') {
    return null;
  }

  return (
    <Card className="subscription-status-card">
      <CardHeader
        title="Subscription"
        subtitle="Manage your NinjaCash Pro subscription"
      />
      
      <div className="subscription-info">
        {/* Status badge */}
        <div className="subscription-row">
          <span className="subscription-label">Plan</span>
          <span className={`subscription-badge subscription-badge--${getStatusVariant(subscriptionStatus)}`}>
            {subscriptionStatus === 'lifetime' ? 'Lifetime Pro' : 'Monthly Pro'}
          </span>
        </div>

        {/* Status */}
        <div className="subscription-row">
          <span className="subscription-label">Status</span>
          <span className={`subscription-status subscription-status--${getStatusVariant(subscriptionStatus)}`}>
            {formatStatus(subscriptionStatus)}
          </span>
        </div>

        {/* Trial end date */}
        {subscriptionStatus === 'trialing' && trialEndDate && (
          <div className="subscription-row">
            <span className="subscription-label">Trial Ends</span>
            <span className="subscription-value">{formatDate(trialEndDate)}</span>
          </div>
        )}

        {/* Past due warning */}
        {subscriptionStatus === 'past_due' && (
          <div className="subscription-warning">
            Your payment is past due. Please update your payment method to avoid losing Pro access.
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="subscription-actions">
        {/* Manage subscription button */}
        {canManage && (
          <Button
            variant="secondary"
            onClick={handleManageSubscription}
            disabled={portalLoading}
          >
            {portalLoading ? 'Opening...' : 'Manage Subscription'}
          </Button>
        )}

        {/* Upgrade to lifetime (for monthly users) */}
        {subscriptionStatus !== 'lifetime' && (
          <Button
            variant="primary"
            onClick={onUpgrade}
          >
            Upgrade to Lifetime
          </Button>
        )}
      </div>

      {/* Portal error */}
      {portalError && (
        <div className="subscription-error">
          {portalError}
        </div>
      )}

      {/* Help text */}
      <p className="subscription-help">
        {subscriptionStatus === 'lifetime' 
          ? 'You have lifetime access. View invoices or update billing info in the portal.'
          : 'Update payment method, view invoices, or cancel anytime.'}
      </p>
    </Card>
  );
}

export default SubscriptionStatus;
